import { LeadStatus } from "../Lead";
import { VehicleStatus } from "../Vehicle";
import { DefaultResponseWithData } from "../system";

// Wire values are ints - keep in sync with GPMetals.Core/Entities/PartRequest.cs
export enum PartRequestSource {
  Internal = 0,
  PartPro = 1,
}

export enum PartRequestStatus {
  Open = 0,
  Claimed = 1,
  Fulfilled = 2,
  Closed = 3,
}

export enum PartRequestContactPreference {
  Email = 0,
  Phone = 1,
}

// GPMetals.Core/Helpers/PartRequestRules.cs
export enum PartRequestMatchStrength {
  None = 0,
  Possible = 1,
  Likely = 2,
  Exact = 3,
}

export interface IPartRequestVehicleMatch {
  vehicleId: string;
  vrm: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  stockNumber: string | null;
  status: VehicleStatus;
  strength: PartRequestMatchStrength;
  paintMatch: boolean;
}

/** A car on a lead that hasn't been booked in yet, which may have the part. */
export interface IPartRequestLeadMatch {
  leadId: string;
  leadStatus: LeadStatus;
  leadCreated: string;
  vehicleId: string;
  vrm: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  strength: PartRequestMatchStrength;
  paintMatch: boolean;
}

/** GET /partrequest/for-lead/{leadId} - a live request the car on the lead may be able to supply. */
export interface IPartRequestLeadAlert {
  partRequestId: string;
  source: PartRequestSource;
  status: PartRequestStatus;
  partWanted: string;
  partNumber: string | null;
  paintCode: string | null;
  vrm: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  requestedAt: string;
  strength: PartRequestMatchStrength;
  paintMatch: boolean;
}

export interface IPartRequestLeadAlertsResponse extends DefaultResponseWithData<IPartRequestLeadAlert[]> {}

export interface IPartRequestOffer {
  id: string;
  externalConfirmationId: string;
  vendorName: string | null;
  vendorPhone: string | null;
  vendorCity: string | null;
  message: string | null;
  listingId: string | null;
  listingTitle: string | null;
  listingTagNumber: string | null;
  listingUrl: string | null;
  conversationId: string | null;
  offeredAt: string;
}

export interface IPartRequest {
  id: string;
  created: string;
  updated: string | null;

  source: PartRequestSource;
  externalId: string | null;

  vrm: string | null;
  make: string | null;
  model: string | null;
  year: number | null;
  partWanted: string;
  details: string | null;
  partCategoryId: string | null;
  partCategoryName: string | null;
  engineCode: string | null;
  stockSearchId: string | null;
  /** Yard-owned on both sources - PartPro's sync never touches these. */
  partNumber: string | null;
  paintCode: string | null;

  status: PartRequestStatus;
  contactPreference: PartRequestContactPreference;

  customerName: string | null;
  customerEmail: string | null;
  customerPhone: string | null;

  externalCreatedAt: string | null;
  requestedAt: string;

  claimedOn: string | null;
  claimedById: string | null;
  claimMessage: string | null;
  conversationId: string | null;

  linkedVehicleId: string | null;
  linkedVehicleVrm: string | null;
  linkedPartId: string | null;
  linkedPartTagNumber: string | null;

  networkExternalId: string | null;
  networkPublishedOn: string | null;
  networkClosedOn: string | null;
  isOnNetwork: boolean;
  canPublishToNetwork: boolean;
  canClaim: boolean;

  createdById: string | null;
  lastSyncedOn: string | null;

  offerCount: number;
  matchedVehicles: IPartRequestVehicleMatch[];
  /** Detail page only - the first few; matchedLeadCount is the full total. */
  matchedLeads: IPartRequestLeadMatch[];
  matchedLeadCount: number;
  offers: IPartRequestOffer[];
}

export interface IPartRequestResponse extends DefaultResponseWithData<IPartRequest> {}

/** GET /partrequest/status - drives which PartPro actions the pages show. */
export interface IPartRequestIntegrationStatus {
  featureEnabled: boolean;
  partProConnected: boolean;
}

export interface IPartRequestIntegrationStatusResponse extends DefaultResponseWithData<IPartRequestIntegrationStatus> {}

export interface IPartRequestFilter {
  source?: PartRequestSource;
  status?: PartRequestStatus;
  includeClosed?: boolean;
  onlyMine?: boolean;
  onlyMatched?: boolean;
}

export interface ICreatePartRequest {
  customerName: string;
  customerPhone?: string | null;
  customerEmail?: string | null;
  contactPreference: PartRequestContactPreference;
  vrm?: string | null;
  make?: string | null;
  model?: string | null;
  year?: number | null;
  partWanted: string;
  details?: string | null;
  /** Links the request to a category so it shows on a matching vehicle's parts list. */
  partCategoryId?: string | null;
  engineCode?: string | null;
  /** The stock search the request was raised from, when it came from the Stock page. */
  stockSearchId?: string | null;
  partNumber?: string | null;
  paintCode?: string | null;
}

export interface IPartRequestYardDetails {
  partNumber: string | null;
  paintCode: string | null;
}

export interface IClaimPartRequest {
  message?: string | null;
  vehicleId?: string | null;
  partId?: string | null;
}
